import { useDisclosure } from '@mantine/hooks'
import { Add, LocationOnOutlined } from '@mui/icons-material'
import { useTheme } from '@mui/material'
import { ComponentProps, useCallback, useMemo, useState } from 'react'

import FormikLabeledAutoComplete, {
  TFormikLabeledAutocompleteOption,
} from '~/components/formikFields/formikLabeledAutocomplete'
import { TPropsWithFieldProps } from '~/components/formikFields/models'
import WrapWithFastField from '~/components/formikFields/wrapWithFastField'
import useGetLocations from '~/features/agents/hooks/useGetLocations'
import { ELocationKeys } from '~/features/agents/models'
import CreateEditLocationModal from '~/features/locations/components/createEditLocationModal'
import rem from '~/utils/ui'

type TProps = {
  hideCreate?: boolean
  onChange?: () => void
} & Omit<
  ComponentProps<typeof FormikLabeledAutoComplete>,
  | 'meta'
  | 'createOption'
  | 'getOptionKey'
  | 'isLoading'
  | 'options'
  | 'startIcon'
  | 'fieldProps'
  | 'name'
> &
  TPropsWithFieldProps<string, object>

const LocationsAutocomplete = WrapWithFastField<TProps, string, object>(
  function Component({
    fieldProps,
    hideCreate = false,
    onChange = undefined,
    ...props
  }) {
    const theme = useTheme()
    const { setFieldValue } = fieldProps.form
    const { name } = fieldProps.field
    const [isCreateOpen, { close: closeCreate, open: openCreate }] =
      useDisclosure(false)
    const [initialLocationName, setInitialLocationName] = useState('')
    const { data, isLoading } = useGetLocations()
    const locations = useMemo(() => data?.data ?? [], [data?.data])

    const locationIcon = useMemo(
      () => (
        <LocationOnOutlined
          sx={{
            color: theme.palette.secondary1,
            fontSize: rem(20),
          }}
        />
      ),
      [theme.palette.secondary1],
    )

    const options = useMemo(
      () =>
        locations.map((location) => ({
          icon: locationIcon,
          label: location[ELocationKeys.name],
          value: location[ELocationKeys.id],
        })),
      [locationIcon, locations],
    )

    const handleCreateClick = useCallback(
      (search: string) => {
        setInitialLocationName(search)
        openCreate()
      },
      [openCreate],
    )

    const handleCloseCreate = useCallback(() => {
      closeCreate()
      setInitialLocationName('')
    }, [closeCreate])

    const handleCreateSuccess = useCallback(
      async (locationId: string) => {
        await setFieldValue(name, locationId)
        onChange?.()
      },
      [name, onChange, setFieldValue],
    )

    const createOption = useMemo(
      () =>
        hideCreate
          ? undefined
          : {
              icon: (
                <Add
                  sx={{
                    color: theme.palette.secondary1,
                    fontSize: rem(20),
                  }}
                />
              ),
              label: 'Add new location',
              onClick: handleCreateClick,
            },
      [handleCreateClick, hideCreate, theme.palette.secondary1],
    )

    const getOptionKey = useCallback(
      (option: string | TFormikLabeledAutocompleteOption) =>
        (option as TFormikLabeledAutocompleteOption).value,
      [],
    )

    const meta = useMemo(
      () => ({
        createOption,
        isLoading,
        options,
      }),
      [createOption, isLoading, options],
    )

    return (
      <>
        <FormikLabeledAutoComplete
          createOption={createOption}
          freeSolo={false}
          getOptionKey={getOptionKey}
          isLoading={isLoading}
          meta={meta}
          name={name}
          onChange={onChange}
          options={options}
          startIcon={locationIcon}
          {...props}
        />
        {!hideCreate && (
          <CreateEditLocationModal
            initialName={initialLocationName}
            isOpen={isCreateOpen}
            onClose={handleCloseCreate}
            onSuccess={handleCreateSuccess}
            subtitle="Create a new location. It will be selected automatically once added."
          />
        )}
      </>
    )
  },
)

export default LocationsAutocomplete
